import {
  Box,
  BoxProps,
  chakra,
  Flex,
  FormControl,
  IconButton,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  ImageUploadMultipleIcon,
  ImageUploadSingleIcon,
  PencilIcon,
  TrashIcon,
} from '@sphere/icons';
import { rem } from 'polished';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { LoadingBar } from '../Loader';
import { MediaPreview } from './MediaPreview';

type Props = {
  /* Name for the hidden `input` element. Defaults to 'file */
  name: string;
  /* Path to an initial image to display */
  value?: string;
  /* Optional testid, defaults to 'image-input' */
  testId?: string;
  /* Change handler called with the file that was selected */
  onChange?: (file?: File) => void;
  /* Change handler called with the files that where selected when the multiple prop is true */
  onChangeMultiple?: (file?: File[]) => void;
  /* Indicates if the input is invalid */
  isInvalid?: boolean;
  /** Required label rendered below the icon */
  label: string;
  /** Optional instructions rendered next to the input */
  instructions?: string;
  /* Optional description rendered below the label */
  description?: string;
  /** Dropzone options */
  dropzoneProps?: DropzoneOptions;
  /** Allow multiple files */
  multiple?: boolean;
  /** Contains the preview for better alignment and representation of for example transparent logos */
  containPreview?: boolean;
  /** If the media input is optional or not, if its optional we hide the remove button */
  optional?: boolean;
  /** If the media upload is still loading or not (renders a loading bar if it is) */
  loading?: boolean;
};

export type MediaInputProps = Props & Omit<BoxProps, 'onChange'>;

export const MediaInput = forwardRef<HTMLInputElement, MediaInputProps>(
  (
    {
      name = 'file',
      label,
      description,
      value,
      onChange,
      onChangeMultiple,
      isInvalid = false,
      rounded = '100%',
      testId = 'image-input',
      instructions,
      dropzoneProps,
      multiple,
      containPreview,
      optional,
      loading,
      ...boxProps
    },
    ref,
  ) => {
    const accept = {
      'image/jpeg': [],
      'image/png': [],
      'image/webp': [],
      'image/gif': [],
      ...dropzoneProps?.accept,
    };

    const [initialValue, setInitialValue] = useState(value);
    const [file, setFile] = useState<File & { preview: string }>();

    useEffect(() => {
      setInitialValue(value);
    }, [value]);

    const onDrop = useCallback((acceptedFiles: File[]) => {
      const file = acceptedFiles?.[0];
      const object = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      setFile(object);
      multiple ? onChangeMultiple && onChangeMultiple(acceptedFiles) : onChange?.(file);
    }, []);

    const onDelete = useCallback(() => {
      setInitialValue(undefined);
      setFile(undefined);
      onChange?.(undefined);
    }, []);

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
      ...dropzoneProps,
      multiple,
      accept,
      onDrop,
    });

    const { size: _size, style: _style, ...inputProps } = getInputProps(); // eslint-disable-line

    return (
      <VStack
        spacing="space.16"
        minWidth="auto"
        alignItems="flex-start"
        w={boxProps.w || boxProps.width}
      >
        <VStack spacing="space.16" w={boxProps.w || boxProps.width}>
          <FormControl
            isInvalid={isInvalid}
            position="relative"
            overflow="hidden"
            // Allows the use of _groupHover on child components
            role="group"
            w={boxProps.w || boxProps.width}
            h={boxProps.h || boxProps.height}
            cursor="pointer"
          >
            <Box
              bg={isDragActive ? 'gray.4' : 'gray.3'}
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius={rounded}
              border="1px dashed"
              borderColor={isDragActive ? 'black' : isInvalid ? 'red' : 'gray.2'}
              _groupHover={{
                borderColor: !isInvalid ? 'gray.5' : 'red',
                bg: 'gray.4',
              }}
              transitionProperty="border-color, background"
              transitionDuration="0.2s"
              transitionTimingFunction="ease"
              _focus={{ outline: 'none' }}
              _focusVisible={{ boxShadow: 'outline' }}
              {...boxProps}
              {...getRootProps()}
            >
              <Box display="flex" flexDir="column" textAlign="center" w="75%">
                {!initialValue && (
                  <>
                    {multiple ? (
                      <ImageUploadMultipleIcon mx="auto" w={rem(36)} opacity={0.7} />
                    ) : (
                      <ImageUploadSingleIcon mx="auto" w={rem(36)} opacity={0.7} />
                    )}
                    <chakra.span mt="space.6" fontSize="sm">
                      {label}
                    </chakra.span>
                  </>
                )}
                {description && (
                  <chakra.span mt="space.4" fontSize="xs" color="gray.5" whiteSpace="pre-line">
                    {description}
                  </chakra.span>
                )}
              </Box>
            </Box>

            {/* Renders a preview of the selected image */}
            {file && !multiple && (
              <MediaPreview
                as={file.type.startsWith('video') ? 'video' : 'img'}
                src={file.preview}
                rounded={rounded}
                zIndex={3}
                // Revoke data uri after image is loaded (memory management)
                onLoad={() => URL.revokeObjectURL(file.preview)}
                containPreview={containPreview}
              />
            )}

            {/* Renders the initial value, if provided to the component */}
            {initialValue && !file && !multiple && (
              <MediaPreview
                as="img"
                src={value}
                rounded={rounded}
                zIndex={2}
                containPreview={containPreview}
              />
            )}

            {(initialValue || file) && (
              <Box
                position="absolute"
                zIndex={5}
                inset={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius={rounded}
                color="white"
                bg="rgba(0, 0, 0, .7)"
                transition="opacity .2s ease"
                opacity={loading ? 0 : isDragActive ? 1 : 0}
                _groupHover={{
                  opacity: loading ? 0 : 1,
                }}
                _focusWithin={{ opacity: loading ? 0 : 1 }}
                pointerEvents="none"
              >
                <IconButton
                  as="div"
                  aria-label={multiple ? 'Edit images' : 'Edit image'}
                  variant="clear"
                  background="none"
                  type="button"
                  onClick={open}
                  icon={<PencilIcon boxSize={rem(20)} />}
                />
                {(value || file) && !multiple && optional && (
                  <IconButton
                    zIndex={6}
                    aria-label={multiple ? 'Remove images' : 'Remove image'}
                    variant="clear"
                    background="none"
                    type="button"
                    onClick={onDelete}
                    icon={<TrashIcon boxSize={rem(24)} />}
                    _hover={{ opacity: 0.6 }}
                    _focusVisible={{ opacity: 0.6, boxShadow: 'outline' }}
                    pointerEvents="all"
                  />
                )}
              </Box>
            )}

            <Flex
              pos="absolute"
              inset={0}
              pointerEvents="none"
              zIndex={5}
              bg="gray.3"
              align="center"
              justify="center"
              border="1px solid"
              borderColor={loading ? 'arcticBlue' : 'transparent'}
              borderRadius={rounded}
              px="space.24"
              flexDir="column"
              transition={`all 250ms ease-in-out${loading ? '' : ' 300ms'}`}
              opacity={loading ? 1 : 0}
            >
              <Text fontWeight="medium" fontSize="sm" mb="space.16" color="gray.6">
                Uploading image...
              </Text>
              <LoadingBar isAnimating={loading} maxW={rem(180)} />
            </Flex>

            {/* Input that captures users click or drop event */}
            <Input
              {...inputProps}
              ref={ref}
              data-testid={testId}
              type="file"
              name={name}
              value=""
              accept=".jpg,.png,.jpeg"
              multiple={multiple}
              display="block"
              position="absolute"
              zIndex={4}
              w="100%"
              h="100%"
              inset={0}
              opacity={0}
              cursor="pointer"
              _focus={{ outline: 'none' }}
            />
          </FormControl>

          <Flex flexDir="column">
            {instructions && (
              <chakra.span fontSize="xs" color="gray.5" whiteSpace="pre-line">
                {instructions}
              </chakra.span>
            )}
          </Flex>
        </VStack>
      </VStack>
    );
  },
);
